import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CaretDown,
  CaretUp,
  CheckCircle,
  XCircle,
  Warning,
  ArrowSquareOut,
  Repeat,
  User,
  ChatCircleText,
} from "@phosphor-icons/react";

/**
 * V9.44 — Inline card rendered below the assistant message that just
 * answered a "lookup_reminder" question. Surfaces each matching
 * reminder with its next fire, recurrence, and last-fire delivery
 * verdict so the user can verify Journey's answer at a glance.
 *
 * Color coding (per user spec):
 *   • Green   = DELIVERED
 *   • Red     = FAILED
 *   • Amber   = NO DELIVERY / NOT DELIVERED / unknown
 *
 * Card is collapsed by default to a one-liner per reminder; tapping
 * expands to show the full record + last-fire details.
 */

const NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function verdictTone(verdict) {
  const v = (verdict || "").toUpperCase();
  if (v.startsWith("DELIVERED")) return "green";
  if (v.startsWith("FAILED")) return "red";
  return "amber"; // NO DELIVERY / NOT DELIVERED / unknown / empty
}

function VerdictBadge({ verdict }) {
  if (!verdict) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.04] text-white/40 border border-white/[0.06]"
        data-testid="reminder-lookup-verdict-unknown"
      >
        no fires yet
      </span>
    );
  }
  const tone = verdictTone(verdict);
  const palette = {
    green: {
      cls: "bg-emerald-500/12 text-emerald-300 border-emerald-400/30",
      Icon: CheckCircle,
    },
    red: {
      cls: "bg-rose-500/12 text-rose-300 border-rose-400/30",
      Icon: XCircle,
    },
    amber: {
      cls: "bg-amber-500/12 text-amber-300 border-amber-400/30",
      Icon: Warning,
    },
  }[tone];
  const { Icon } = palette;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${palette.cls}`}
      data-testid={`reminder-lookup-verdict-${tone}`}
      title={verdict}
    >
      <Icon size={10} weight="fill" />
      {verdict}
    </span>
  );
}

function RecipientBadge({ rem }) {
  // V9.44.2 — Explicit recipient indicator. Owner reminders show
  // "For You" (push); SMS-deferred reminders show "SMS to <label>".
  // Falls back to deriving from `sms_target_phone` for legacy
  // payloads that pre-date the backend's explicit `recipient` field.
  const safeRem = rem || {};
  const recipient =
    safeRem.recipient ||
    (safeRem.sms_target_phone
      ? {
          type: "contact",
          label: safeRem.sms_target_label || "contact",
          channel: "sms",
        }
      : { type: "owner", label: "You", channel: "push" });
  if (recipient.type === "owner") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-violet-500/12 text-violet-200 border border-violet-400/25"
        data-testid="reminder-lookup-recipient-owner"
        title="Owner reminder — push notification to you"
      >
        <User size={10} weight="fill" />
        For You · push
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/12 text-sky-200 border border-sky-400/25"
      data-testid="reminder-lookup-recipient-contact"
      title={`SMS-deferred to ${recipient.label} (${recipient.channel})`}
    >
      <ChatCircleText size={10} weight="fill" />
      SMS to {recipient.label}
    </span>
  );
}

function RecurrencePill({ rem }) {
  if (rem.repeat_type !== "weekly" && rem.repeat_type !== "daily") return null;
  const label = rem.recurrence_label || rem.repeat_type;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-cyan-500/10 text-cyan-300 border border-cyan-400/20"
      data-testid="reminder-lookup-recurrence"
    >
      <Repeat size={10} weight="bold" />
      {label}
    </span>
  );
}

function ReminderRow({ rem }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // V9.44.3 — Defensive: legacy / unexpected payloads (missing fields,
  // non-array recent_fires, null repeat_days_of_week, malformed
  // fired_at) must NEVER crash the row.
  const safeRem = rem || {};
  const recentFires = Array.isArray(safeRem.recent_fires)
    ? safeRem.recent_fires
    : [];
  const lastFire = recentFires[0] || null;
  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };
  const lastFireWhen = formatDate(lastFire && lastFire.fired_at);
  const repeatType = (safeRem.repeat_type || "none").toLowerCase();
  const fireCount = Number(safeRem.fire_count || 0) || 0;
  const rowId = safeRem.id || "unknown";

  return (
    <div
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
      data-testid={`reminder-lookup-row-${rowId}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 hover:bg-white/[0.03] transition-colors"
        data-testid={`reminder-lookup-toggle-${rowId}`}
      >
        <div className="flex items-start gap-2">
          <Bell
            size={14}
            weight="duotone"
            className="text-cyan-300 mt-0.5 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium text-white/90 text-sm truncate">
                {safeRem.title || "(untitled reminder)"}
              </span>
              <RecipientBadge rem={safeRem} />
              <RecurrencePill rem={safeRem} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/55">
              {safeRem.next_fire_local && (
                <span data-testid={`reminder-lookup-next-${rowId}`}>
                  Next:{" "}
                  <span className="text-white/80">{safeRem.next_fire_local}</span>
                </span>
              )}
              <VerdictBadge verdict={lastFire && lastFire.verdict} />
              {lastFireWhen && (
                <span
                  className="text-white/40"
                  data-testid={`reminder-lookup-last-${rowId}`}
                >
                  · last {lastFireWhen}
                </span>
              )}
            </div>
          </div>
          <div className="shrink-0 text-white/40">
            {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </div>
        </div>
      </button>

      {open && (
        <div
          className="px-3 pb-3 pt-1 border-t border-white/[0.04] text-[11px] text-white/55 space-y-2"
          data-testid={`reminder-lookup-expanded-${rowId}`}
        >
          {safeRem.description && (
            <div>
              <span className="text-white/35">Description: </span>
              <span className="text-white/75">{safeRem.description}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            <div>
              <span className="text-white/35">Reminder ID</span>
              <div className="text-white/70 font-mono text-[10px] truncate">
                {rowId}
              </div>
            </div>
            <div>
              <span className="text-white/35">Status</span>
              <div className="text-white/70">{safeRem.status || "pending"}</div>
            </div>
            {repeatType === "weekly" && (
              <div className="col-span-2">
                <span className="text-white/35">Days</span>
                <div className="text-white/70">
                  {(Array.isArray(safeRem.repeat_days_of_week)
                    ? safeRem.repeat_days_of_week
                    : []
                  )
                    .map((d) => NAMES[Number(d)] || `?${d}`)
                    .join(" · ")}
                </div>
              </div>
            )}
            {fireCount > 0 && (
              <div>
                <span className="text-white/35">Times fired</span>
                <div className="text-white/70">{fireCount}</div>
              </div>
            )}
            {safeRem.timezone && (
              <div>
                <span className="text-white/35">Timezone</span>
                <div className="text-white/70">{safeRem.timezone}</div>
              </div>
            )}
          </div>

          {recentFires.length > 0 && (
            <div className="pt-1.5 border-t border-white/[0.04]">
              <div className="text-white/35 mb-1">Recent fires</div>
              <ul
                className="space-y-1"
                data-testid={`reminder-lookup-fires-${rowId}`}
              >
                {recentFires.map((f, i) => {
                  const safeF = f || {};
                  const firedWhen = formatDate(safeF.fired_at);
                  const push = safeF.push || {};
                  const sms = safeF.sms || {};
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2 flex-wrap"
                      data-testid={`reminder-lookup-fire-${rowId}-${i}`}
                    >
                      <VerdictBadge verdict={safeF.verdict} />
                      <span className="text-white/55">
                        {firedWhen || "—"}
                      </span>
                      {push.attempted && (
                        <span className="text-white/40 text-[10px]">
                          push {push.delivered ?? 0}/{push.total_subs ?? 0}
                        </span>
                      )}
                      {sms.attempted && (
                        <span className="text-white/40 text-[10px]">
                          sms {sms.outcome || "?"}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/app/reminders?highlight=${rowId}`);
            }}
            className="inline-flex items-center gap-1 mt-1 text-cyan-300 hover:text-cyan-200 transition-colors"
            data-testid={`reminder-lookup-open-${rowId}`}
          >
            Open in Reminders <ArrowSquareOut size={11} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ReminderLookupChip({ lookup }) {
  if (!lookup || typeof lookup !== "object") return null;
  const reminders = Array.isArray(lookup.reminders) ? lookup.reminders : [];
  if (reminders.length === 0) {
    return (
      <div
        className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/8 border border-amber-400/20 text-[11px] text-amber-200"
        data-testid="reminder-lookup-empty"
      >
        <Warning size={12} weight="duotone" className="text-amber-300" />
        No matching reminders found
        {lookup.query ? ` for "${lookup.query}"` : ""}
      </div>
    );
  }
  return (
    <div
      className="mt-2 w-full max-w-[440px] space-y-1.5"
      data-testid={`reminder-lookup-card-${reminders.length}`}
    >
      {reminders.map((rem, i) => (
        <ReminderRow key={(rem && rem.id) || `idx-${i}`} rem={rem} />
      ))}
    </div>
  );
}
